import { classNames } from "@/utils/classNames";
import { Icon, IconProps } from "./Icon";
import { observer } from "mobx-react";
import {
  forwardRef,
  Fragment,
  HTMLAttributes,
  ReactElement,
  useCallback,
  useContext,
  useRef,
} from "react";
import { spacingClassName } from "./tailwindMappings";
import { renderIcon } from "./iconHelpers";
import { isReactElement } from "@/utils/reactHelpers";
import { isTMessage, isTMessageParam, TMessage, TMessageParam } from "@/i18n/tt";
import { when } from "@/utils/functions";
import {
  DCCallback,
  DCFocusEventHandler,
  useCallbackHandler,
  useDispatch,
  useFocusEventHandler,
  useI18n,
  useTranslation,
} from "@/stores/RootStoreContext";
import { DCEvent } from "@/events/eventsDomain";
import { RadioFieldModel } from "@/common/commonForms";
import { TableContext } from "./TableContext";
import { IconName } from "./iconMapping";
import { BgColor, bgColorClassName } from "./colors";
import { PreppedI18nState } from "@/i18n/i18nDomain";
import { Select as BaseSelect } from "@base-ui/react/select";
import { groupBy, map } from "lodash";
import { Box } from "./Box";
import { Button } from "./Button";

export type SelectProps<T> = {
  id?: string;
  testId?: string;
  label?: string | TMessage;
  placeholder?: string;
  disabled?: boolean;
  icon?: IconProps["name"];
  name?: string;
  value?: T;
  items: T[] | ((i18n: PreppedI18nState) => T[]);
  onSelect: (item: T) => void | DCEvent[];
  onFocus?: DCFocusEventHandler;
  onBlur?: DCFocusEventHandler;
  onClose?: DCCallback;
  idFn: (item: T) => string;
  itemLabelFn?: (item: T, i18n: PreppedI18nState) => SelectItemProps["label"];
  labelFn: (item: T, i18n: PreppedI18nState) => SelectItemProps["label"];
  iconFn?: (item: T) => SelectItemProps["icon"];
  captionFn?: (item: T, i18n: PreppedI18nState) => SelectItemProps["caption"];
  sectionFn?: (item: T) => TMessage | undefined;
  disabledReasonFn?: (item: T, i18n: PreppedI18nState) => TMessage | string | undefined;
  fullWidth?: boolean;
  variant?: "in-table";
  compact?: boolean;
  error?: boolean;
  message?: string;
  autofocus?: boolean;
  iconOnly?: boolean;
  tooltip?: TMessage | string;
  rounded?: boolean | "left" | "right";
  grow?: boolean;
  bg?: BgColor;
  border?: boolean;
  className?: string;
  labelSrOnly?: boolean;
  dropdownOptions?: {
    alignment: "start" | "center" | "end";
  };
  size?: "md" | "sm";
};

type SelectButtonProps<T> = SelectProps<T> & {
  open?: boolean;
  buttonRef: (el: HTMLButtonElement | null) => void;
};

function SelectButton<T>({
  placeholder,
  disabled,
  icon,
  value,
  labelFn,
  captionFn,
  iconFn,
  open,
  variant,
  testId,
  buttonRef,
  fullWidth,
  label,
  name,
  error,
  message,
  iconOnly,
  grow = true,
  bg = "default",
  border = true,
  rounded = true,
  className,
  labelSrOnly,
  size = "md",
  ...props
}: SelectButtonProps<T>) {
  const i18n = useI18n();
  const { tf } = i18n;
  const id = props.id;
  const tableContext = useContext(TableContext);
  const inTable = variant === "in-table" || tableContext?.editable;
  const compact = props.compact || tableContext?.compact;
  const valueLabel =
    value !== undefined
      ? when(labelFn(value, i18n), (v) => (isReactElement(v) ? v : tf(v)))
      : undefined;
  const caption = value !== undefined ? captionFn?.(value, i18n) : undefined;
  const valueIcon = value !== undefined && iconFn ? iconFn(value) : undefined;
  const focusHandler = useFocusEventHandler(props.onFocus);
  const blurHandler = useFocusEventHandler(props.onBlur);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltip = tf(props.tooltip);

  return (
    <div
      ref={containerRef}
      data-test-type={"select-label"}
      className={classNames("min-w-0", "flex", "flex-col", grow && "grow", fullWidth && "w-full")}
      aria-label={tooltip}
      data-tooltip={tooltip}
    >
      {label && (
        <BaseSelect.Label
          className={classNames("block mb-1", (inTable || labelSrOnly) && "sr-only")}
        >
          {typeof label === "string" ? label : tf(label)}
        </BaseSelect.Label>
      )}
      <BaseSelect.Trigger
        ref={buttonRef}
        id={id}
        name={name}
        data-testid={testId}
        data-test-type={"select"}
        className={classNames(
          className,
          "flex",
          "grow",
          "items-center",
          compact ? "px-1" : "px-4",
          size === "sm" ? "text-sm" : "text-base",
          "leading-tight",
          border && !inTable && "border",
          !inTable &&
            (rounded === "left" ? "rounded-l" : rounded === "right" ? "rounded-r" : "rounded-sm"),
          "focus:ring-1",
          "outline-hidden",
          "cursor-pointer",
          disabled
            ? ["subtle", "bg-gray-x-soft", "border-gray-soft", "hover:border-gray-soft"]
            : error
              ? [
                  "color-default",
                  bgColorClassName(bg),
                  "border-error",
                  "focus-within:border-error",
                  "focus-within:ring-error",
                  "hover:border-error",
                  "hover:focus-within:border-error",
                ]
              : [
                  "color-default",
                  bgColorClassName(bg),
                  "border-gray",
                  "focus-within:border-primary",
                  "focus-within:ring-primary",
                  "hover:border-gray-hard",
                  "hover:focus-within:border-primary",
                ],
        )}
        onClick={(e) => e.stopPropagation()}
        onFocus={focusHandler}
        onBlur={open ? undefined : blurHandler}
      >
        {when(valueIcon || icon, (i) => (
          <div className={classNames("text-xl leading-5 h-5 subtle flex items-center")}>
            {renderIcon(i)}
          </div>
        ))}
        {iconOnly && <span className={compact ? "py-1" : "py-3"}>&nbsp;</span>}
        <BaseSelect.Value
          className={classNames(
            iconOnly && "sr-only",
            "flex-1",
            "focus-visible:outline-hidden",
            compact ? "py-1" : size === "sm" ? "py-2" : "py-3",
            valueIcon || icon ? "mx-2" : "mr-2",
            "bg-transparent",
            "disabled:bg-transparent",
            "text-left",
            "truncate",
          )}
        >
          {valueLabel ? (
            valueLabel
          ) : placeholder ? (
            <div className={"subtle"}>{placeholder}</div>
          ) : (
            <>&nbsp;</>
          )}
        </BaseSelect.Value>
        {caption && (
          <div className={classNames("subtle mr-2", iconOnly && "sr-only")}>
            {isTMessage(caption) || isTMessageParam(caption) ? tf(caption) : caption}
          </div>
        )}
        <BaseSelect.Icon
          className={classNames("text-xl leading-5 h-5 pointer-events-none flex-none")}
          render={(_props, state) => <Icon name={state.open ? "caret-up" : "caret-down"} />}
        ></BaseSelect.Icon>
      </BaseSelect.Trigger>
      {message && (
        <div className={classNames("pt-1", "pl-4", "text-sm", error ? "color-error" : "subtle")}>
          {message}
        </div>
      )}
    </div>
  );
}

export type SelectItemProps = {
  label: ReactElement | TMessage | TMessageParam | string | number | undefined;
  caption?: ReactElement | TMessage | TMessageParam | string | number;
  icon?: ReactElement | IconProps | IconName;
  active?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  tooltip?: TMessage | string;
} & HTMLAttributes<HTMLDivElement>;

export const SelectItem = forwardRef<HTMLDivElement, SelectItemProps>(function SelectItem(
  { label, icon, caption, active, isDisabled, tooltip, ...props }: SelectItemProps,
  ref,
) {
  const { tf } = useTranslation();
  return (
    <div
      {...props}
      ref={ref}
      data-tooltip={typeof tooltip === "string" ? tooltip : tooltip ? tf(tooltip) : undefined}
      className={classNames(
        props.className,
        spacingClassName("px", "sm"),
        "flex",
        "gap-2",
        "items-center",
        "py-2",
        isDisabled ? "cursor-default opacity-25" : "cursor-pointer",
        "min-w-40",
        "color-default",
        "outline-hidden",
        "user-select-none",
        active ? "bg-primary-light" : "bg-default",
      )}
    >
      {icon && renderIcon(icon)}
      {isTMessage(label) || isTMessageParam(label)
        ? tf(label)
        : label === undefined
          ? "\u00a0"
          : label}
      {caption && (
        <div className={"subtle ml-auto"}>
          {isTMessage(caption) || isTMessageParam(caption) ? tf(caption) : caption}
        </div>
      )}
    </div>
  );
});

export const Select = observer(<T,>(props: SelectProps<T>) => {
  const dispatch = useDispatch();
  const i18n = useI18n();
  const items = Array.isArray(props.items) ? props.items : props.items(i18n);
  const groups: { value: string; items: T[] }[] = map(
    groupBy(items, (item) => i18n.tf(props.sectionFn?.(item) ?? "")),
    (groupItems, groupName) => ({ value: groupName, items: groupItems }),
  );
  const buttonRef = useCallback(
    (ref: HTMLButtonElement | null) => {
      if (props.autofocus) {
        ref?.click();
      }
    },
    [props.autofocus],
  );
  const onClose = useCallbackHandler(props.onClose);

  return (
    <BaseSelect.Root
      disabled={props.disabled}
      value={props.value}
      items={groups}
      isItemEqualToValue={(itemValue, selectedValue) =>
        props.idFn(itemValue) === props.idFn(selectedValue)
      }
      onValueChange={(item, details) => {
        if (item === null && details.reason === "none") {
          return;
        }
        when(props.onSelect(item as T), dispatch);
      }}
      onOpenChange={(open) => {
        if (!open) {
          onClose?.();
        }
      }}
    >
      <SelectButton buttonRef={buttonRef} {...props} />
      <BaseSelect.Portal>
        <BaseSelect.Positioner
          align={props.dropdownOptions?.alignment ?? "start"}
          alignItemWithTrigger={false}
        >
          <BaseSelect.Popup
            style={{ minWidth: "var(--anchor-width)", transformOrigin: "var(--transform-origin)" }}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <BaseSelect.List
              className={classNames(
                "rounded-sm",
                "border",
                "border-gray",
                "shadow-lg",
                "bg-default",
                "inline-flex",
                "flex-col",
                "py-1",
                "overflow-auto",
                "relative",
              )}
              style={{ maxHeight: "var(--available-height)" }}
            >
              {groups.map((group) => {
                const renderedGroupItems = group.items.map((item) => {
                  const disabledReason = props.disabledReasonFn?.(item, i18n);
                  return (
                    <BaseSelect.Item
                      key={props.idFn(item)}
                      value={item}
                      disabled={disabledReason !== undefined}
                      render={(itemProps, state) => (
                        <div
                          {...itemProps}
                          style={{ minWidth: "var(--anchor-width)" }}
                          className="outline-hidden"
                        >
                          <SelectItem
                            label={
                              props.itemLabelFn
                                ? props.itemLabelFn(item, i18n)
                                : props.labelFn(item, i18n)
                            }
                            icon={props.iconFn?.(item)}
                            caption={props.captionFn?.(item, i18n)}
                            active={state.highlighted}
                            isDisabled={disabledReason !== undefined}
                            tooltip={disabledReason}
                          />
                        </div>
                      )}
                    />
                  );
                });
                return group.value ? (
                  <BaseSelect.Group key={group.value}>
                    <div className="py-1">
                      <hr />
                    </div>
                    <BaseSelect.GroupLabel className="px-2 pt-2 cursor-default text-xs subtitle">
                      {group.value}
                    </BaseSelect.GroupLabel>
                    {renderedGroupItems}
                  </BaseSelect.Group>
                ) : (
                  <Fragment key={group.value}>{renderedGroupItems}</Fragment>
                );
              })}
            </BaseSelect.List>
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  );
});

export type MSelectProps<T> = {
  model: RadioFieldModel<T>;
  name?: SelectProps<T>["name"];
  icon?: SelectProps<T>["icon"];
  label?: SelectProps<T>["label"];
  placeholder?: SelectProps<T>["placeholder"];
  onSelect?: SelectProps<T>["onSelect"];
  onFocus?: SelectProps<T>["onFocus"];
  onBlur?: SelectProps<T>["onBlur"];
  onClose?: SelectProps<T>["onClose"];
  idFn?: SelectProps<T>["idFn"];
  labelFn?: SelectProps<T>["labelFn"];
  itemLabelFn?: SelectProps<T>["itemLabelFn"];
  iconFn?: SelectProps<T>["iconFn"];
  disabledReasonFn?: SelectProps<T>["disabledReasonFn"];
  disabled?: boolean;
  fullWidth?: boolean;
  id?: string;
  testId?: string;
  error?: boolean;
  message?: string;
  autofocus?: boolean;
  compact?: boolean;
  tooltip?: TMessage | string;
};

export const MSelect = observer(function MSelect<T>({
  model,
  onSelect,
  ...props
}: MSelectProps<T>) {
  const { t } = useTranslation();
  return (
    <Select
      {...props}
      value={model.value}
      items={model.options}
      onSelect={onSelect ?? model.setValue}
      idFn={model.getId ?? props.idFn}
      labelFn={props.labelFn ?? model.params.labelFn}
      iconFn={props.iconFn ?? model.params.iconFn}
      captionFn={model.params.descriptionFn}
      disabled={props.disabled}
      error={!model.isValid}
      message={when(model.error, t)}
    />
  );
});

export type SelectViewProps<T> = Pick<
  SelectProps<T>,
  "value" | "labelFn" | "iconFn" | "icon" | "placeholder"
> & { readonly?: boolean };

export const SelectView = observer(function SelectView<T>({
  value,
  labelFn,
  iconFn,
  icon,
  placeholder,
  readonly,
}: SelectViewProps<T>) {
  const i18n = useI18n();
  const { tf } = i18n;
  const tableContext = useContext(TableContext);
  const compact = tableContext?.compact;
  const valueLabel =
    value !== undefined
      ? when(labelFn(value, i18n), (v) => (isReactElement(v) ? v : tf(v)))
      : undefined;
  const valueIcon = value !== undefined && iconFn ? iconFn(value) : undefined;

  return (
    <div
      tabIndex={0}
      className={classNames(
        "flex w-full items-center leading-tight outline-hidden cursor-pointer",
        compact ? "px-1" : "px-4",
      )}
    >
      {when(valueIcon || icon, (i) => (
        <div className="text-xl leading-5 h-5 subtle flex items-center">{renderIcon(i)}</div>
      ))}
      <div
        className={classNames(
          "flex-1 truncate text-left",
          compact ? "py-1" : "py-3",
          valueIcon || icon ? "mx-2" : "mr-2",
        )}
      >
        {valueLabel ? (
          valueLabel
        ) : placeholder ? (
          <div className="subtle">{placeholder}</div>
        ) : (
          <>&nbsp;</>
        )}
      </div>
      {!readonly && (
        <div className="text-xl leading-5 h-5 pointer-events-none flex-none">
          <Icon name="caret-down" />
        </div>
      )}
    </div>
  );
});

export type MSelectViewProps<T> = {
  model: RadioFieldModel<T>;
  labelFn?: SelectProps<T>["labelFn"];
  iconFn?: SelectProps<T>["iconFn"];
  placeholder?: SelectProps<T>["placeholder"];
  readonly?: boolean;
};

export const MSelectView = observer(function MSelectView<T>({
  model,
  labelFn,
  iconFn,
  placeholder,
  readonly,
}: MSelectViewProps<T>) {
  return (
    <SelectView
      value={model.value}
      labelFn={labelFn ?? model.params.labelFn}
      iconFn={iconFn ?? model.params.iconFn}
      placeholder={placeholder}
      readonly={readonly}
    />
  );
});

export type SelectWithNavigatorProps<T> = SelectProps<T> & {
  selectNext?: () => void;
  selectPrev?: () => void;
};

export const SelectWithNavigator = <T,>({
  selectNext,
  selectPrev,
  ...props
}: SelectWithNavigatorProps<T>) => (
  <Box className="min-w-0 max-sm:grow">
    <Box className={"w-full md:w-56 min-w-0"}>
      <Select fullWidth {...props} />
    </Box>
    <Button
      fullWidth={false}
      variant={"basic"}
      icon={"caret-left"}
      tooltip={{ key: "previous" }}
      onClick={selectPrev}
      disabled={!selectPrev}
    />
    <Button
      fullWidth={false}
      variant={"basic"}
      icon={"caret-right"}
      tooltip={{ key: "next" }}
      onClick={selectNext}
      disabled={!selectNext}
    />
  </Box>
);
